// src/pages/Admin/GestaoInstituicaoPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Tooltip, Divider, Switch, InputAdornment, Tabs, Tab
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  HourglassEmpty as TrialIcon,
  History as HistoryIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { obterDetalheInstituicaoAdmin, ajustarModuloInstituicao } from '../../services/billingService';

const STATUS_COLOR = { PENDENTE: 'warning', CONFIRMADO: 'success', REJEITADO: 'error', CANCELADO: 'default' };

function TabPanel({ children, value, index }) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}
TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

export default function GestaoInstituicaoPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [erro, setErro]         = useState('');
  const [sucesso, setSucesso]   = useState('');
  const [tab, setTab]           = useState(0);

  // Dialog de edição
  const [dialogo, setDialogo]   = useState({ aberto: false, modulo: null });
  const [form, setForm]         = useState({});
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await obterDetalheInstituicaoAdmin(id);
      setDados(res);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao carregar dados da instituição');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirEdicao = (modulo) => {
    setForm({
      ativo:               modulo.ativo ?? false,
      em_trial:            modulo.em_trial ?? false,
      trial_fim:           modulo.trial_fim ? modulo.trial_fim.slice(0, 10) : '',
      quota_plagio_limite: modulo.quota_plagio_limite ?? '',
      quota_ia_limite:     modulo.quota_ia_limite ?? '',
      preco_negociado_mzn: modulo.preco_negociado_mzn != null ? (modulo.preco_negociado_mzn / 100) : '',
    });
    setDialogo({ aberto: true, modulo });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const payload = {
        ativo:               form.ativo,
        em_trial:            form.em_trial,
        trial_fim:           form.trial_fim || null,
        quota_plagio_limite: form.quota_plagio_limite !== '' ? parseInt(form.quota_plagio_limite) : null,
        quota_ia_limite:     form.quota_ia_limite     !== '' ? parseInt(form.quota_ia_limite)     : null,
        preco_negociado_mzn: form.preco_negociado_mzn !== '' ? Math.round(parseFloat(form.preco_negociado_mzn) * 100) : null,
      };
      await ajustarModuloInstituicao(id, dialogo.modulo.codigo, payload);
      setSucesso(`Módulo "${dialogo.modulo.nome}" actualizado com sucesso`);
      setDialogo({ aberto: false, modulo: null });
      carregar();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao guardar alterações');
    } finally {
      setSalvando(false);
    }
  };

  const estadoModulo = (m) => {
    if (!m.mi_id || !m.ativo) return 'inativo';
    if (m.em_trial) {
      return m.trial_fim && new Date(m.trial_fim) < new Date() ? 'trial_expirado' : 'trial';
    }
    return 'ativo';
  };

  const chipEstado = (m) => {
    const e = estadoModulo(m);
    if (e === 'ativo')          return <Chip icon={<ActiveIcon />}   label="Activo"          color="success" size="small" />;
    if (e === 'trial')          return <Chip icon={<TrialIcon />}    label={`Trial até ${new Date(m.trial_fim).toLocaleDateString('pt-PT')}`} color="warning" size="small" />;
    if (e === 'trial_expirado') return <Chip icon={<InactiveIcon />} label="Trial expirado"  color="error"   size="small" />;
    return                             <Chip icon={<InactiveIcon />} label="Inactivo"         color="default" size="small" />;
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  const { instituicao, modulos = [], historico = [] } = dados ?? {};

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Cabeçalho */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/admin/instituicoes')}><BackIcon /></IconButton>
        <Box flexGrow={1}>
          <Typography variant="h5" fontWeight={700}>{instituicao?.nome}</Typography>
          <Typography variant="body2" color="text.secondary">{instituicao?.email} · {instituicao?.tipoInstituicao}</Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={carregar}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {erro    && <Alert severity="error"   onClose={() => setErro('')}    sx={{ mb: 2 }}>{erro}</Alert>}
      {sucesso && <Alert severity="success" onClose={() => setSucesso('')} sx={{ mb: 2 }}>{sucesso}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab icon={<TuneIcon />}    iconPosition="start" label="Módulos & Quotas" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label={`Histórico (${historico.length})`} />
      </Tabs>

      {/* ── TAB 1: Módulos ─────────────────────────────────────────────── */}
      <TabPanel value={tab} index={0}>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Módulo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Preço mensal</TableCell>
                <TableCell align="right">Preço negociado</TableCell>
                <TableCell align="center">Plágio usado / limite</TableCell>
                <TableCell align="center">IA usado / limite</TableCell>
                <TableCell align="center">Reset quotas</TableCell>
                <TableCell align="center">Editar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modulos.map(m => (
                <TableRow key={m.codigo} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{m.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.codigo}</Typography>
                    {m.eh_addon && <Chip label="add-on" size="small" color="info" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{chipEstado(m)}</TableCell>
                  <TableCell align="right">
                    {m.preco_mensal_mzn ? `${(m.preco_mensal_mzn / 100).toLocaleString('pt-MZ')} MZN` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {m.preco_negociado_mzn != null
                      ? <Chip label={`${(m.preco_negociado_mzn / 100).toLocaleString('pt-MZ')} MZN`} size="small" color="primary" variant="outlined" />
                      : <Typography variant="caption" color="text.secondary">padrão</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {m.mi_id
                      ? `${m.quota_plagio_usado ?? 0} / ${m.quota_plagio_limite ?? '∞'}`
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {m.mi_id
                      ? `${m.quota_ia_usado ?? 0} / ${m.quota_ia_limite ?? '∞'}`
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {m.quota_reset_em
                      ? <Typography variant="caption">{new Date(m.quota_reset_em).toLocaleDateString('pt-PT')}</Typography>
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar configurações">
                      <IconButton size="small" onClick={() => abrirEdicao(m)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* ── TAB 2: Histórico ───────────────────────────────────────────── */}
      <TabPanel value={tab} index={1}>
        {historico.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhum pedido registado para esta instituição.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>#</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Observações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{p.modulo_nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.modulo_codigo}</Typography>
                    </TableCell>
                    <TableCell><Chip label={p.tipo_cobranca} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right">{p.valor_mzn ? (p.valor_mzn / 100).toLocaleString('pt-MZ') + ' MZN' : '—'}</TableCell>
                    <TableCell>{p.metodo_pagamento}</TableCell>
                    <TableCell><Typography variant="body2" fontFamily="monospace">{p.referencia_pagamento || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{new Date(p.created_at).toLocaleDateString('pt-PT')}</Typography></TableCell>
                    <TableCell><Chip label={p.status} color={STATUS_COLOR[p.status] || 'default'} size="small" /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {p.observacoes || p.motivo_rejeicao || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ── Dialog de edição ───────────────────────────────────────────── */}
      <Dialog open={dialogo.aberto} onClose={() => setDialogo({ aberto: false, modulo: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <TuneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configurar módulo — {dialogo.modulo?.nome}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={!!form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />
                <Typography variant="body2">Módulo activo</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={!!form.em_trial} onChange={e => setForm(f => ({ ...f, em_trial: e.target.checked }))} />
                <Typography variant="body2">Em trial</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Data de fim do trial" type="date"
                value={form.trial_fim}
                onChange={e => setForm(f => ({ ...f, trial_fim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Quotas mensais (deixar vazio = ilimitado)</Typography></Divider></Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Limite plágio / mês" type="number"
                value={form.quota_plagio_limite}
                onChange={e => setForm(f => ({ ...f, quota_plagio_limite: e.target.value }))}
                placeholder="ilimitado"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Limite IA / mês" type="number"
                value={form.quota_ia_limite}
                onChange={e => setForm(f => ({ ...f, quota_ia_limite: e.target.value }))}
                placeholder="ilimitado"
              />
            </Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Preço negociado (opcional)</Typography></Divider></Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Preço negociado" type="number"
                value={form.preco_negociado_mzn}
                onChange={e => setForm(f => ({ ...f, preco_negociado_mzn: e.target.value }))}
                placeholder="Usar preço padrão"
                InputProps={{ endAdornment: <InputAdornment position="end">MZN</InputAdornment> }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogo({ aberto: false, modulo: null })} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}
            startIcon={salvando ? <CircularProgress size={16} /> : null}>
            {salvando ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
